import { BOT_INTENTS } from '../constants/intents';
import { ChatbotContext } from './chatbotContext';

export interface ShipmentCheckResult {
  requiresShipmentNumber: boolean;
  hasShipmentNumber: boolean;
  shipmentNumber?: string;
  promptMessage?: string;
  intent: string;
}

// Map of intents requiring a shipment number to their user-facing prompt message
export const SHIPMENT_NUMBER_INTENTS: Record<string, string> = {
  [BOT_INTENTS.TRACK_SHIPMENT]: 'Please provide the shipment number to track your shipment.',
  [BOT_INTENTS.SHIPMENT_STATUS]: 'Please provide the shipment number to check its status.',
  [BOT_INTENTS.DOWNLOAD_AWB]: 'Please provide the shipment number to download the label/AWB.',
  [BOT_INTENTS.REPEAT_SHIPMENT]: 'Please provide the shipment number you would like to repeat.',
  [BOT_INTENTS.CANCEL_SHIPMENT]: 'Please provide the shipment number you wish to cancel.',
  [BOT_INTENTS.UPDATE_SHIPMENT]: 'Please provide the shipment number you want to update.',
  [BOT_INTENTS.UPDATE_SHIPMENT_STATUS]: 'Please provide the shipment number to update its status.',
};

// Generic phrases that indicate an intent without a specific shipment number
const GENERIC_SHIPMENT_PHRASES = [
  'track shipment',
  'track shipments',
  'track',
  'track order',
  'find shipment',
  'tracking',
  'track a shipment',
  'track my shipment',
  'shipment status',
  'check status',
  'check shipment status',
  'view shipment status',
  'download label',
  'download awb',
  'download carrier awb',
  'get label',
  'get awb',
  'print label',
  'download shipment label',
  'repeat shipment',
  'repeat order',
  'reorder shipment',
  'duplicate shipment',
  'cancel shipment',
  'cancel order',
  'cancel my shipment',
  'update shipment',
  'edit shipment',
  'update shipment status',
];

/**
 * Checks if a message/query text contains an explicit shipment number.
 */
export function extractShipmentNumberFromText(text: string): string | null {
  if (!text || typeof text !== 'string') return null;
  const trimmed = text.trim();
  const lower = trimmed.toLowerCase();

  // If the entire text is just a generic phrase, there is no shipment number in it
  if (GENERIC_SHIPMENT_PHRASES.includes(lower)) {
    return null;
  }

  // Check if there is something remaining after stripping the generic prefix
  const stripped = trimmed
    .replace(
      /^(track shipment|track|shipment status|repeat shipment|download label|download carrier awb|download awb|cancel shipment|update shipment status|update shipment|for|to)\s+/i,
      '',
    )
    .trim();

  if (stripped && stripped.length > 0 && stripped.length < 50) {
    if (!GENERIC_SHIPMENT_PHRASES.includes(stripped.toLowerCase())) {
      return stripped;
    }
  }

  // Check for common shipment number token pattern (e.g. SB12345, alphanumeric with digits)
  const patternMatch = trimmed.match(/\b([A-Za-z0-9_-]{5,30})\b/);
  if (patternMatch) {
    const candidate = patternMatch[1];
    if (!GENERIC_SHIPMENT_PHRASES.some(p => p === candidate.toLowerCase())) {
      return candidate;
    }
  }

  return null;
}

/**
 * Evaluates whether an action requires a shipment number and whether one is available
 * from route context, extracted entities, or the query text.
 */
export function checkShipmentNumberRequirement(
  text: string,
  intent: string | null | undefined,
  context: ChatbotContext,
  extractedEntities?: Record<string, unknown> | null,
): ShipmentCheckResult {
  let matchedIntent = intent;

  if (!matchedIntent) {
    // Attempt to infer intent from text if not provided
    const lower = text.trim().toLowerCase();
    if (lower.includes('track') || lower.includes('find shipment')) {
      matchedIntent = BOT_INTENTS.TRACK_SHIPMENT;
    } else if (lower.includes('status')) {
      matchedIntent = BOT_INTENTS.SHIPMENT_STATUS;
    } else if (lower.includes('label') || lower.includes('awb')) {
      matchedIntent = BOT_INTENTS.DOWNLOAD_AWB;
    } else if (lower.includes('repeat')) {
      matchedIntent = BOT_INTENTS.REPEAT_SHIPMENT;
    } else if (lower.includes('cancel')) {
      matchedIntent = BOT_INTENTS.CANCEL_SHIPMENT;
    }
  }

  if (!matchedIntent || !SHIPMENT_NUMBER_INTENTS[matchedIntent]) {
    return {
      requiresShipmentNumber: false,
      hasShipmentNumber: false,
      intent: matchedIntent || '',
    };
  }

  const promptMessage = SHIPMENT_NUMBER_INTENTS[matchedIntent];

  // Check 1: Route context (e.g. /shipment/history/:shipmentNumber)
  if (context.shipmentNumber) {
    return {
      requiresShipmentNumber: true,
      hasShipmentNumber: true,
      shipmentNumber: String(context.shipmentNumber),
      promptMessage,
      intent: matchedIntent,
    };
  }

  // Check 2: Extracted entities from uploaded documents
  if (extractedEntities?.shipmentNumber) {
    return {
      requiresShipmentNumber: true,
      hasShipmentNumber: true,
      shipmentNumber: String(extractedEntities.shipmentNumber),
      promptMessage,
      intent: matchedIntent,
    };
  }

  // Check 3: Query text contains a shipment number
  const extractedFromText = extractShipmentNumberFromText(text);
  if (extractedFromText) {
    return {
      requiresShipmentNumber: true,
      hasShipmentNumber: true,
      shipmentNumber: extractedFromText,
      promptMessage,
      intent: matchedIntent,
    };
  }

  // Shipment number is required by this intent, but not found in context, entities, or text
  return {
    requiresShipmentNumber: true,
    hasShipmentNumber: false,
    promptMessage,
    intent: matchedIntent,
  };
}
